import { useCallback } from "react";
import type { KeyboardEvent, PointerEvent } from "react";
import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconLayoutSidebarRightCollapse,
  IconLayoutSidebarRightExpand,
} from "@tabler/icons-react";
import { PANEL_LIMITS, type EditorPanel } from "../../hooks/useEditorPanels";
import styles from "./PanelEdge.module.scss";

const KEYBOARD_STEP = 16;

const PANEL_NAMES = { left: "fields panel", right: "properties panel" } as const;

interface PanelEdgeProps {
  side: "left" | "right";
  panel: EditorPanel;
}

export function PanelEdge({ side, panel }: PanelEdgeProps) {
  const { width, max: maxWidth, collapsed, resize, resizeTo, toggle: onToggle } = panel;
  const { min: minWidth, initial: defaultWidth } = PANEL_LIMITS[side];
  const panelName = PANEL_NAMES[side];
  // Dragging right widens the left panel but narrows the right one.
  const direction = side === "left" ? 1 : -1;

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = width;
      const target = event.currentTarget;
      target.setPointerCapture(event.pointerId);
      const move = (ev: globalThis.PointerEvent) => {
        // Measured from where the drag began, so a clamped step never makes the edge lag the pointer.
        resizeTo(startWidth + (ev.clientX - startX) * direction);
      };
      const stop = () => {
        target.removeEventListener("pointermove", move);
        target.removeEventListener("pointerup", stop);
        target.removeEventListener("pointercancel", stop);
      };
      target.addEventListener("pointermove", move);
      target.addEventListener("pointerup", stop);
      target.addEventListener("pointercancel", stop);
    },
    [direction, resizeTo, width],
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      resizeTo(event.key === "Home" ? minWidth : maxWidth);
      return;
    }
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const sign = event.key === "ArrowRight" ? 1 : -1;
    resize(sign * KEYBOARD_STEP * direction);
  };

  const CollapseIcon =
    side === "left" ? IconLayoutSidebarLeftCollapse : IconLayoutSidebarRightCollapse;
  const ExpandIcon = side === "left" ? IconLayoutSidebarLeftExpand : IconLayoutSidebarRightExpand;

  if (collapsed) {
    return (
      <div className={styles.rail} data-side={side}>
        <button
          type="button"
          className={styles.railButton}
          onClick={onToggle}
          aria-expanded={false}
          aria-label={`Show ${panelName}`}
          title={`Show ${panelName}`}
        >
          <ExpandIcon size={18} aria-hidden />
          <span className={styles.railLabel}>{panelName}</span>
        </button>
      </div>
    );
  }

  return (
    <div className={styles.edge} data-side={side}>
      <button
        type="button"
        className={styles.toggle}
        onClick={onToggle}
        aria-expanded
        aria-label={`Hide ${panelName}`}
        title={`Hide ${panelName}`}
      >
        <CollapseIcon size={16} aria-hidden />
      </button>
      <div
        className={styles.handle}
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${panelName}`}
        aria-valuenow={Math.round(width)}
        aria-valuemin={minWidth}
        aria-valuemax={maxWidth}
        tabIndex={0}
        title="Drag to resize · double-click to reset"
        onPointerDown={handlePointerDown}
        onKeyDown={handleKeyDown}
        onDoubleClick={() => resizeTo(defaultWidth)}
      />
    </div>
  );
}
